'use client';

import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { ImageToVideo } from '@/components/videos/ImageToVideo';
import { BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { AssetCreationToolName, GenerationInstrument, ICardMediaAsset, Image, MediaAssetType } from '@/types/media';
import { useImageToVideo } from '@/hooks/video/useImageToVideo';
import { PricesService } from '@/features/billing';
import { useHasCreatedSearchProducts } from '@/features/products/hooks/useHasCreatedSearchProducts';
import { Product } from '@/types/product';
import ProductSelect from '@/components/ProductSelect';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { useInsufficientBalance } from '@/features/billing/components/InsufficientBalanceContext';

const CREDITS = PricesService.getPriceText(AssetCreationToolName.animateImage, [GenerationInstrument.imageToVideo]);
const CREDITS_TEXT = CREDITS ? ` (${CREDITS})` : '';

export const AiVideoModalSection = ({ product }: ImagesModalSectionProps) => {
  const { hasCreatedSearchProducts } = useHasCreatedSearchProducts();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(product || null);
  const [videoPrompt, setVideoPrompt] = useState<string>();
  const { checkBalance } = useInsufficientBalance();
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const productImages = selectedProduct?.images;
  const { isError, isGenerating, generatedMediaAsset, handleGenerate } = useImageToVideo();

  const handleSelectClick = useCallback(
    (image: ICardMediaAsset) => {
      if (selectedImage?.src === image.src) {
        setSelectedImage(null);
      } else {
        setSelectedImage(image as Image);
      }
    },
    [selectedImage]
  );

  const items = useMemo(
    () =>
      (productImages || []).map((image) => ({
        ...image,
        type: MediaAssetType.image,
      })),
    [productImages]
  );

  const renderItem = useCallback(
    (asset) => {
      return (
        <MediaAssetCard
          size="small"
          hideUseAction
          aspectRatio="portrait"
          media={asset}
          key={asset.id}
          selected={selectedImage?.src === asset.src}
          onSelect={handleSelectClick}
        />
      );
    },
    [handleSelectClick, selectedImage]
  );

  const handleGenerateClick = () => {
    if (!checkBalance(AssetCreationToolName.animateImage, [GenerationInstrument.imageToVideo])) return;
    handleGenerate({ imageSrc: selectedImage?.src, videoPrompt });
  };

  return (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd" fontWeight="bold">
        Create AI Video
      </Text>
      <Card>
        <BlockStack gap="200">
          <Text as="p" variant="bodyMd">
            Products
          </Text>
          {hasCreatedSearchProducts && (
            <ProductSelect onSelect={setSelectedProduct} selectedProduct={selectedProduct} />
          )}
        </BlockStack>
      </Card>

      <MediaGrid items={items} size="medium" renderItem={renderItem} />
      <ImageToVideo
        disabled={!selectedImage}
        setVideoPrompt={setVideoPrompt}
        videoPrompt={videoPrompt || ''}
        isError={isError}
        isGenerating={isGenerating}
        generatedMediaAsset={generatedMediaAsset as ICardMediaAsset}
        hideUseAction
      />

      <InlineStack align="end">
        <Button
          size="large"
          onClick={handleGenerateClick}
          disabled={isGenerating || !selectedImage?.src}
          loading={isGenerating}
        >
          Generate{CREDITS_TEXT}
        </Button>
      </InlineStack>
    </BlockStack>
  );
};
